import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { OptionType } from 'src/app/core/enums/enums';
import { ContractError, StepMiniState } from 'src/app/core/models/models';
import * as ReRouteActions from '../actions/re-route.actions';
import { GenericError } from 'src/app/layout/actions/layout.actions';
import { Passenger, PnrInfo } from 'src/app/core/models/itinerary.model';
import { DOCSADoc } from 'src/app/core/models/docsa.model';
import {
  IReRouteModalQuote,
  IReRoutePassengerData,
  IReRouteQuoteRequest
} from 'src/app/core/models/re-route.model';
import {
  aditionalInformationStep,
  initialStepNextAditionalInformation,
  reRouteQuoteRequestFailStep,
  reRouteQuoteRequestSuccessStep
} from './re-route-modal-steps';

export interface ReRouteState {
  receivedFrom: string;
  modalQuote: IReRouteModalQuote;
  docsaDocs: DOCSADoc[];
  selectedPassengers: Passenger[];
  reRouteQuoteRequest: IReRouteQuoteRequest;
  steps: StepMiniState;
  loading: boolean;
  newPnr: PnrInfo;
  isError: boolean;
  error: ContractError;
}

export interface State {
  reRoute: ReRouteState;
}

export const initialState: ReRouteState = {
  modalQuote: null,
  docsaDocs: [],
  selectedPassengers: [],
  reRouteQuoteRequest: null,
  steps: aditionalInformationStep,
  loading: false,
  newPnr: null,
  isError: false,
  error: null,
  receivedFrom: null
};

const reRouteReducer = createReducer(
  initialState,
  on(GenericError, (state, { error }) => ({
    ...state,
    loading: false,
    isError: true,
    error
  })),
  on(ReRouteActions.saveReRouteModalQuote, (state, { reRouteModalQuote }) => ({
    ...state,
    modalQuote: reRouteModalQuote,
    loading: false,
    isError: false,
    error: null
  })),
  on(ReRouteActions.DocsaSave, (state, { docs }) => ({
    ...state,
    docsaDocs: docs
  })),

  on(ReRouteActions.saveReRouteQuoteRequest, (state, { reRouteQuoteRequest }) => ({
    ...state,
    reRouteQuoteRequest,
    loading: false,
    isError: false,
    error: null
  })),
  on(ReRouteActions.saveStepsState, (state, { step }) => ({
    ...state,
    steps: step,
    loading: false,
    isError: false,
    error: null
  })),
  on(ReRouteActions.saveSelectedPassengers, (state, { selectedPassengers }) => ({
    ...state,
    selectedPassengers,
    loading: false,
    isError: false,
    error: null
  })),
  on(ReRouteActions.SetReceivedFrom, (state, { receivedFrom }) => ({
    ...state,
    receivedFrom
  })),
  on(ReRouteActions.RemoveReceivedFrom, (state) => ({
    ...state,
    receivedFrom: null
  })),
  on(ReRouteActions.updateReRouteQuoteRequest, (state, { updatedData }) => ({
    ...state,
    reRouteQuoteRequest: {
      ...state.reRouteQuoteRequest,
      pnr: updatedData.pnr,
      country: updatedData.country,
      gds: updatedData.gds,
      bookingCode: updatedData.bookingCode,
      receivedFrom: state.receivedFrom,
      transactionId: updatedData.transactionId,
      officeId: updatedData.officeId,
      passengers: updatedData.passengers as IReRoutePassengerData[]
    },
    loading: false,
    isError: false,
    error: null
  })),
  on(ReRouteActions.ForceRefreshSesionAndReRoute, (state) => ({
    ...state,
    loading: true,
    isError: false,
    error: null
  })),
  on(ReRouteActions.reRouteQuoteRequest, (state) => ({
    ...state,
    loading: true,
    isError: false,
    error: null
  })),
  on(ReRouteActions.reRouteQuoteRequestSuccess, (state, { pnr }) => ({
    ...state,
    steps: reRouteQuoteRequestSuccessStep,
    loading: false,
    isError: false,
    newPnr: pnr,
    error: null
  })),
  on(ReRouteActions.reRouteInitialStep, (state) => ({
    ...state,
    steps: initialStepNextAditionalInformation,
    loading: false,
    isError: false,
    error: null
  })),
  on(ReRouteActions.reRouteQuoteRequestFail, (state, { error }) => ({
    ...state,
    steps: reRouteQuoteRequestFailStep,
    loading: false,
    isError: true,
    error
  })),
  on(ReRouteActions.updateLoading, (state, { isLoading }) => ({
    ...state,
    loading: isLoading
  })),
  on(ReRouteActions.resetReRouteState, () => ({ ...initialState }))
);

export function reducer(state: ReRouteState | undefined, action: Action): ReRouteState {
  return reRouteReducer(state, action);
}

export const selectReRoute = createFeatureSelector<ReRouteState>(OptionType.ReRoute);

export const selectLoading = createSelector(selectReRoute, (state: ReRouteState) => state.loading);

export const selectPnrInfo = createSelector(selectReRoute, (state: ReRouteState) => state.newPnr);

export const selectIsError = createSelector(selectReRoute, (state: ReRouteState) => state.isError);

export const selectError = createSelector(selectReRoute, (state) => state.error);

export const selectDocsaDocs = createSelector(
  selectReRoute,
  (state: ReRouteState) => state.docsaDocs
);

export const selectModalQuote = createSelector(
  selectReRoute,
  (state: ReRouteState) => state.modalQuote
);

export const selectQuoteRequest = createSelector(
  selectReRoute,
  (state: ReRouteState) => state.reRouteQuoteRequest
);

export const selectStepsState = createSelector(selectReRoute, (state: ReRouteState) => state.steps);

export const selectSelectedPassengers = createSelector(
  selectReRoute,
  (state: ReRouteState) => state.selectedPassengers
);
